'use client';

import { Radio as BaseRadio } from '@base-ui-components/react/radio';
import { RadioGroup as BaseRadioGroup } from '@base-ui-components/react/radio-group';
import { Circle } from 'lucide-react';

import { cn } from '../../utils/cn';
import { focusVisibleStyles } from '../../utils/focus';
import './radio.css';

interface RadioGroupProps {
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  disabled?: boolean;
  required?: boolean;
  name?: string;
  orientation?: 'horizontal' | 'vertical';
  className?: string;
  children: React.ReactNode;
}

function RadioGroup({
  className,
  orientation = 'vertical',
  children,
  onValueChange,
  ...props
}: RadioGroupProps) {
  const groupProps = {
    onValueChange: onValueChange
      ? (val: unknown) => onValueChange(val as string)
      : undefined,
    ...props,
  } as React.ComponentProps<typeof BaseRadioGroup>;

  return (
    <BaseRadioGroup
      aria-orientation={orientation}
      {...groupProps}
      className={cn(
        'flex',
        orientation === 'vertical' ? 'flex-col gap-2' : 'flex-row gap-2',
        className
      )}
    >
      {children}
    </BaseRadioGroup>
  );
}

interface RadioGroupItemProps {
  value: string;
  disabled?: boolean;
  className?: string;
  children?: React.ReactNode;
}

function RadioGroupItem({
  className,
  children,
  ...props
}: RadioGroupItemProps) {
  return (
    <label
      className={cn(
        'flex items-center gap-2',
        props.disabled && 'cursor-not-allowed opacity-50',
        !props.disabled && 'cursor-pointer'
      )}
    >
      <BaseRadio.Root
        className={cn(
          'radio-indicator-root',
          'border-border flex h-4 w-4 shrink-0 items-center justify-center rounded-full border',
          'data-[checked]:border-primary data-[checked]:text-primary',
          'data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50',
          focusVisibleStyles,
          className
        )}
        {...props}
      >
        <BaseRadio.Indicator className="radio-indicator flex items-center justify-center">
          <Circle className="h-2 w-2 fill-current" />
        </BaseRadio.Indicator>
      </BaseRadio.Root>
      {children && (
        <span className="text-sm leading-none font-medium">{children}</span>
      )}
    </label>
  );
}

export { RadioGroup, RadioGroupItem };
export type { RadioGroupProps, RadioGroupItemProps };
